import { generateId } from "@cayuse/shared-react.utils.helpers";
import {
	isArray,
	isObject,
	isNonEmptyArray,
	isNonEmptyObject,
	isNonEmptyString,
	isNumber,
	isObjectWithKey,
} from "@cayuse/shared-react.utils.type-checks";
import { _ } from "@flowable/forms";
import { cloneDeep, filter, includes, isString, isEmpty, map } from "lodash";
import { api } from "services";
import { dispatch } from "store";
import { v4 as uuidv4, validate as uuidValidate } from "uuid";


import { mapValueToCodes, parseExpression } from "components/flowable/ClassificationCodes/helpers";
import { validateIsRequired } from "components/flowable/ResearchTeam/utils";
import {
	CAYUSE_TYPES,
	COLUMN_HEADER_KEYS,
	FIELD_TYPES,
	FUNDING_PROGRAM_OBJECT_DEFAULT_MISSING_COUNT,
	RT_OBJECT_DEFAULT_MISSING_COUNT,
	RULE_TYPES,
} from "globals/constants";
import { RESEARCH_TEAM } from "globals/constants/formObjects";
import { form as constants } from "store/constants";
import { formSelectors } from "store/selectors";
import { getDateFormat } from "utils/date";

import { isNonEmpty, parseJSON, slugifyString, spUrl } from "../helpers";
import { findAdHocTeamById } from "../picklists";


const { ASSOCIATION_TYPE } = RESEARCH_TEAM;

export const cayusePicklistTypes = [
	CAYUSE_TYPES.ORG_FINDER,
	CAYUSE_TYPES.UNIT_FINDER,
	CAYUSE_TYPES.PERSON_FINDER,
	CAYUSE_TYPES.FUNDING_PROGRAM,
	CAYUSE_TYPES.RESEARCH_TEAM,
];

export const picklistTypes = [
	FIELD_TYPES.SELECT,
	FIELD_TYPES.STRING_LIST,
	FIELD_TYPES.RADIO
];

const BLANK_KEY = "BLANK_KEY";
export const VALIDATION_ERROR_DUPLICATE_KEY = "Duplicate key";
export const VALIDATION_ERROR_BLANK_KEY = "Blank key";
export const VALIDATION_ERROR_BLANK_LABEL = "Blank label";
export const VALIDATION_ERROR_VALUE_STARTS_WITH_NUMBER = "Value attribute cannot begin with a number";
export const VALIDATION_ERROR_IGNORE_ATTR_INVALID_VALUE = "Ignore attribute contains a blank, not null value";
export const VALIDATION_ERROR_VISIBLE_ATTR_INVALID_VALUE = "Visible attribute contains a blank, not null value";
export const VALIDATION_ERROR_ENABLED_ATTR_INVALID_VALUE = "Enable attribute contains a blank, not null value";

export const mapType = type => {
	switch (type) {
	case "person-finder":
	case "unit-finder":
	case "org-finder":
		return "select";
	case "research-team":
		return "researchTeam";
	case "classification-codes":
		return "classificationCodes";
	case "cayuse-irb-study":
		return "cayuseIrbStudy";
	case "cayuse-summary-budget-importer":
		return "cayuseSummaryBudgetImporter";
	case "cayuse-proposals-s2s-record":
		return "cayuseProposalsS2SRecord";
	case "cayuse-human-ethics-study-search":
		return "cayuseHumanEthicsStudySearch";
	case "cayuse-animal-oversight":
		return "cayuseAnimalOversight";
	case "cayuse-hazard-safety":
		return "cayuseHazardSafety";
	case "cayuse-haplo-costing-tool":
		return "cayuseHaploCostingTool";
	case "cayuse-haplo-ethics-monitor":
		return "cayuseHaploEthicsMonitor";
	case "cayuse-haplo-repository":
		return "cayuseHaploRepository";
	case "cayuse-disclosure-form-component":
		return "cayuseCOIComponent";
	case "expression-field":
		return "expressionField";
	case "upload":
		return "fileInput";
	default:
		return type;
	}
};

export const findElementAndSectionWithType = (modelJson, type) => {
	if (isNonEmptyArray(modelJson.rows)) {
		// for each row
		for (let row of modelJson.rows) {
			if (isNonEmptyArray(row.cols)) {
				for (let col of row.cols) {
					// in each column
					if (col.type === "tabs" && isNonEmptyArray(col?.extraSettings?.sections)) {
						// if it's a tab, iterate through each section
						for (let section of col?.extraSettings?.sections) {
							if (isNonEmptyArray(section?.extraSettings?.layoutDefinition?.rows)) {
								// for each row in tab panel
								for (let panelRow of section?.extraSettings?.layoutDefinition?.rows) {
									if (isNonEmptyArray(panelRow.cols)) {
										// for each column in row of tab panel
										for (let panelCol of panelRow.cols) {
											// if col is of requested type, return type and section
											if (panelCol.type === type) {
												return {
													sectionLabel: section.label,
													elementLabel: panelCol.label
												};
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}

	return null;
};



export const removeElementWithTypeAndReturnSection = (modelJson, type) => {
	if (isNonEmptyArray(modelJson.rows)) {
		// for each row
		for (let row of modelJson.rows) {
			if (isNonEmptyArray(row.cols)) {
				for (let col of row.cols) {
					// in each column
					if (col.type === "tabs" && isNonEmptyArray(col?.extraSettings?.sections)) {
						// if it's a tab, iterate through each section
						for (let section of col?.extraSettings?.sections) {
							if (isNonEmptyArray(section?.extraSettings?.layoutDefinition?.rows)) {
								// for each row in tab panel
								for (let panelRow of section?.extraSettings?.layoutDefinition?.rows) {
									if (isNonEmptyArray(panelRow.cols)) {
										// for each column in row of tab panel
										for (let panelCol of panelRow.cols) {
											if (panelCol.type === type) {
												// remove col from row
												panelRow.cols = panelRow.cols.filter(col => col.type !== "subform");
												// if row is empty, remove it from section (to prevent save button from activating)
												if (panelRow.cols.length === 0) {
													section.extraSettings.layoutDefinition.rows = section.extraSettings.layoutDefinition.rows
														.filter(section => section.cols.length > 0);
												}

												// return section label
												return section.label;
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}

	return null;
};


export const transformFetchedFormModel = formModel => {
	const model = cloneDeep(formModel);

	if (isNonEmptyArray(model.rows)) {
		model.rows.forEach(row => {
			if (isNonEmptyArray(row.cols)) {
				row.cols.forEach(col => {
					if (isNonEmptyArray(col.extraSettings?.layoutDefinition?.rows)) {
						col.extraSettings.layoutDefinition.rows.forEach(row => {
							if (isNonEmptyArray(row.cols)) {
								row.cols.forEach(col => {
									if (isNonEmptyArray(col.extraSettings?.sections)) {
										col.extraSettings.sections.forEach(section => {
											if (isNonEmptyArray(section.extraSettings?.layoutDefinition?.rows)) {
												section.extraSettings.layoutDefinition.rows.forEach(row => {
													if (isNonEmptyArray(row.cols)) {
														row.cols.forEach(col => {
															col = transformFormElement(col);
														});
													}
												});
											}
										});
									}
								});
							}
						});
					}
				});
			}
		});
	}

	return model;
};

export const transformFetchedPreviewFormModel = formModel => {
	const model = cloneDeep(formModel);

	if (isNonEmptyArray(model.rows)) {
		model.rows.forEach(row => {
			if (isNonEmptyArray(row.cols)) {
				row.cols.forEach(col => {
					if (isNonEmptyArray(col.extraSettings?.sections)) {
						col.extraSettings.sections.forEach(section => {
							if (isNonEmptyArray(section.extraSettings?.layoutDefinition?.rows)) {
								section.extraSettings.layoutDefinition.rows.forEach(row => {
									if (isNonEmptyArray(row.cols)) {
										row.cols.forEach(col => {
											transformFormElement(col);
										});
									}
								});
							}
						});
					}
				});
			}
		});
	}

	return model;
};

export const isFieldPicklist = field => {
	if (!isObjectWithKey(field, "cayuseType") && !isObjectWithKey(field, "type")) {
		return false;
	}

	const cayuseType = isNonEmptyString(field.cayuseType)
		? field.cayuseType
		: undefined;

	const type = isNonEmptyString(field.type)
		? field.type
		: undefined;

	return cayusePicklistTypes.includes(cayuseType) || picklistTypes.includes(type);
};

export const isFieldCheckbox = field => field.type === FIELD_TYPES.CHECKBOX;

const getAssociationDetails = async memberId => {
	const { members } = formSelectors.rto();

	const member = members.find(m => m.memberId === memberId);

	let associationDetails;

	if (member) {
		associationDetails = member.associationDetails;
	} else if (memberId) {
		const response = await api.person.get({ id: memberId, includeInactiveAffiliations: true });

		associationDetails = response?.associations;

		dispatch({ type: constants.SET_RTO_MEMBER_ASSOCIATION_DETAILS, payload: { memberId: memberId, associationDetails } });
	}

	return associationDetails;
};

export const getRTObjectMissingCount = async ({
	RTObjectValueKey,
	formContent = {},
	extraSettings = {},
}) => {
	let rtObject = formContent[RTObjectValueKey];

	const {
		// default values for existing records
		costShareEffortRequired = false,
		creditFieldRequired = true,
		sponsoredEffortRequired = false,
		creditField,
		sponsoredEffort,
		costShareEffort,
		allowNewPerson,
	} = extraSettings;

	if (!rtObject) {
		let totalMissingCount = RT_OBJECT_DEFAULT_MISSING_COUNT;

		creditField && creditFieldRequired && totalMissingCount++;

		return totalMissingCount;
	} else {
		if (typeof rtObject == "string" ) {
			rtObject = parseJSON(rtObject);
		}
		let totalMissingCount = 0;

		if (!isArray(rtObject)) {
			return totalMissingCount;
		}

		for (const entry of rtObject) {
			if (allowNewPerson && entry.guest) {
				if(!entry.guest.firstName) {
					totalMissingCount++;
				}
				if(!entry.guest.lastName) {
					totalMissingCount++;
				}
			} else if(!entry.member) {
				totalMissingCount++;
			}

			!entry.role && totalMissingCount++;

			const associationDetails = await getAssociationDetails(entry.member) || [];

			if (entry.associationType === ASSOCIATION_TYPE.EXTERNAL) {
				if (!entry.externalAssociations || entry.externalAssociations.length === 0) {
					totalMissingCount++;
				} else {
					for (const externalAssociation of entry.externalAssociations) {
						!externalAssociation.association && totalMissingCount++;
					}
				}
				continue;
			}

			for (const association of entry.associations) {
				!association.association && totalMissingCount++;

				if (creditField && creditFieldRequired) {
					const credit = association.credit,
						hasCredit = isNumber(credit),
						isCreditZeroOrGreater = hasCredit && credit >= 0;

					!isCreditZeroOrGreater && totalMissingCount++;
				}

				const singleAssociationDetails = associationDetails.find(a => a.id === association.association);

				if (!singleAssociationDetails) {
					continue;
				}

				if (singleAssociationDetails.isCalendarAppointment) {
					if (costShareEffort && costShareEffortRequired) {
						const calendarEffort = association.costShareEffort.calendar,
							calendarEffortMonths = association.costShareEffortMonths.calendar,
							hasEffort = (isNumber(calendarEffort) && isNumber(calendarEffortMonths)),
							isEffortZeroOrGreater = hasEffort && (calendarEffort >= 0 || calendarEffortMonths >= 0);

						!isEffortZeroOrGreater && totalMissingCount ++;
					}

					if (sponsoredEffort && sponsoredEffortRequired) {
						const calendarEffort = association.sponsoredEffort.calendar,
							calendarEffortMonths = association.sponsoredEffortMonths.calendar,
							hasEffort = (isNumber(calendarEffort) && isNumber(calendarEffortMonths)),
							isEffortZeroOrGreater = hasEffort && (calendarEffort >= 0 || calendarEffortMonths >= 0);

						!isEffortZeroOrGreater && totalMissingCount ++;
					}
				}

				if (singleAssociationDetails.isAcademicAppointment) {
					if (costShareEffort && costShareEffortRequired) {
						const academicEffort = association.costShareEffort.academic,
							academicEffortMonths = association.costShareEffortMonths.academic,
							hasAcademicEffort = (isNumber(academicEffort) && isNumber(academicEffortMonths)),
							isAcademicEffortZeroOrGreater = hasAcademicEffort && (academicEffort >= 0 || academicEffortMonths >= 0);

						!isAcademicEffortZeroOrGreater && totalMissingCount ++;
					}

					if (sponsoredEffort && sponsoredEffortRequired) {
						const academicEffort = association.sponsoredEffort.academic,
							academicEffortMonths = association.sponsoredEffortMonths.academic,
							hasAcademicEffort = (isNumber(academicEffort) && isNumber(academicEffortMonths)),
							isAcademicEffortZeroOrGreater = hasAcademicEffort && (academicEffort >= 0 || academicEffortMonths >= 0);

						!isAcademicEffortZeroOrGreater && totalMissingCount ++;
					}
				}

				if (singleAssociationDetails.isSummerAppointment) {
					if (costShareEffort && costShareEffortRequired) {
						const summerEffort = association.costShareEffort.summer,
							summerEffortMonths = association.costShareEffortMonths.summer,
							hasSummerEffort = (isNumber(summerEffort) && isNumber(summerEffortMonths)),
							isSummerEffortZeroOrGreater = hasSummerEffort && (summerEffort >= 0 || summerEffortMonths >= 0);

						!isSummerEffortZeroOrGreater && totalMissingCount ++;
					}

					if (sponsoredEffort && sponsoredEffortRequired) {
						const summerEffort = association.sponsoredEffort.summer,
							summerEffortMonths = association.sponsoredEffortMonths.summer,
							hasSummerEffort = (isNumber(summerEffort) && isNumber(summerEffortMonths)),
							isSummerEffortZeroOrGreater = hasSummerEffort && (summerEffort >= 0 || summerEffortMonths >= 0);

						!isSummerEffortZeroOrGreater && totalMissingCount ++;
					}
				}
			}
		}

		return totalMissingCount;
	}
};

export const getClassificationCodesMissingCount = ({
	isRequired,
	formContent,
	key,
	includePercentage,
	codeType,
	...rest
}) => {
	if (!isRequired || !formContent) {
		return 0;
	}

	let data = formContent[key];

	if (!isNonEmptyArray(data)) {
		const minimumNumberOfCodes = rest?.[`${codeType.toLowerCase()}Minimum`];
		data = mapValueToCodes(data, minimumNumberOfCodes);
	}

	let codePercentageMissingCount = 0;

	if (includePercentage) {
		const { sum, count } = data.reduce((acc, { percentage }) => {
			if (isNumber(percentage)) {
				acc.sum += percentage;
			} else {
				acc.count += 1;
			}

			return acc;
		}, { sum: 0, count: 0 });

		if (sum !== 100) {
			codePercentageMissingCount = data.length;
		} else {
			codePercentageMissingCount = count;
		}
	}

	const codesMissingCount = data.reduce((acc, item) => {
		if (!item.code) {
			acc++;
		}

		return acc;
	}, 0);

	return codePercentageMissingCount + codesMissingCount;
};

/**
 *
 * @param {Object} params
 * @param {SavedProgram[] | undefined} params.value
 *
 * @return {number}
 */
export const getFundingProgramObjectMissingCount = ({
	value,
}) => {
	if (isNonEmptyArray(value)) {
		return value.reduce((acc, item) => {
			if (!item.id) {
				acc++;
			}
			if (item.roundId?.length === 0) {
				acc++;
			}

			return acc;
		}, 0);
	}

	return FUNDING_PROGRAM_OBJECT_DEFAULT_MISSING_COUNT;
};

export const getBudgetBuilderObjectMissingCount = value => {
	if (value && value.inputType === "builder") {
		return 0;
	}

	const expectedKeys = [
		"total_direct_costs",
		"total_sponsor_costs",
		"external_share",
		"internal_share",
		"total_project_cost",
		"F&A",
		"total_cost_share"
	];

	if (!value || !value.fileData) {
		return expectedKeys.length;
	}

	const fileData = value.fileData;
	let missingCount = 0;

	for (const key of expectedKeys) {
		const raw = fileData[key];

		if (
			raw === undefined ||
			raw === null ||
			raw === "" ||
			isNaN(Number(raw)) ||
			Number(raw) < 0
		) {
			missingCount++;
		}
	}

	return missingCount;
};


/**
 *
 * @param {Array} value
 *
 * @return {boolean}
 */
export const fileUploadCompleted = value => {
	if(isNonEmptyArray(value)) {
		return value.some(({ completedAt, lastModified }) => completedAt || lastModified);
	}

	return false;
};

/**
 *
 * @param {string} value
 *
 * @return {boolean}
 */
export const isRichTextEmpty = value => {
	if (!value) {
		return true;
	}
	const tempElement = document.createElement("div");
	tempElement.innerHTML = value;

	return tempElement.textContent.trim().length === 0;
};

/**
 * A flowable field is required when all of the following conditions are true:
 * 1. the `isRequired` attribute (boolean | array of string expressions) is true
 * 2. the `visible` attribute (boolean | string expression) is true
 * 3. the `enabled` attribute (boolean | string expression) is true
 * 4. the `ignore` attribute (boolean | string expression) is true
 * 5. the field is empty
 */
export const isFormFieldRequired = ({
	valueKey = "",
	isRequired = false,
	visible = true,
	enabled = true,
	formContent = {},
	type = "",
	ignore = false,
}) => {
	if (isString(isRequired) && isRequired.includes("flw.mapAttr")) {
		isRequired = parseExpression(formContent, isRequired);
	}

	const isFieldRequired = isString(isRequired) ? _.evalExpression(isRequired, { ...formContent }) : isRequired;
	const isEnabled = isFormFieldEnabled({
		visible,
		enabled,
		formContent,
		ignore,
	});

	if (
		[
			FIELD_TYPES.BUDGET_BUILDER,
			FIELD_TYPES.RESEARCH_TEAM,
			FIELD_TYPES.CLASSIFICATION_CODES,
			FIELD_TYPES.FUNDING_PROGRAM,
			FIELD_TYPES.UPLOAD,
			FIELD_TYPES.RICH_TEXT,
			FIELD_TYPES.ONE
		].includes(type)
	) {
		return Boolean(isFieldRequired && isEnabled);
	} else {
		const isEmpty = isFormFieldEmpty(valueKey, type, formContent);

		return Boolean(isFieldRequired && isEnabled && isEmpty);
	}
};

/**
 * A flowable field is enabled when all of the following conditions are true:
 * 2. the `visible` attribute (boolean | string expression) is true
 * 3. the `enabled` attribute (boolean | string expression) is true
 * 4. the `ignore` attribute (boolean | string expression) is true
 */
export const isFormFieldEnabled = ({
	visible = true,
	enabled = true,
	formContent = {},
	ignore = false,
}) => {
	if (isString(visible) && visible.includes("flw.mapAttr")) {
		visible = parseExpression(formContent, visible);
	}

	if (isString(ignore) && ignore.includes("flw.mapAttr")) {
		ignore = parseExpression(formContent, ignore);
	}

	const isVisible = isString(visible) ? _.evalExpression(visible, { ...formContent }) : visible;
	const isEnabled = isString(enabled) ? _.evalExpression(enabled, { ...formContent }) : enabled;
	const isIgnore = !(isString(ignore) ? _.evalExpression(ignore, { ...formContent }) : ignore);

	return Boolean(isVisible && isEnabled && isIgnore);
};

/**
 * Checks if a form field is empty.
 *
 * @param {string} valueKey - The key of the value to check in the form content.
 * @param {string} type - The type of the form field.
 * @param {object} formContent - The content of the form.
 * @returns {boolean} True if the form field is empty, false otherwise.
 */
const isFormFieldEmpty = (valueKey, type, formContent) => {
	// Get all the keys in the form content
	const filledOutFormContentKeys = Object.keys(formContent);

	// Check if the valueKey is not in the form content keys
	let isEmpty = !filledOutFormContentKeys.includes(valueKey);

	if (!isEmpty) {
		// Get the value of the form field
		const fieldValue = formContent[valueKey];

		// Check if fieldValue is an empty array
		if (Array.isArray(fieldValue) && !fieldValue.length) {
			isEmpty = true;
		}

		// Check if fieldValue is a boolean and is false
		if (type === "boolean" && !fieldValue) {
			isEmpty = true;
		}

		// Check if fieldValue is of type FIELD_TYPES.CAYUSE_CHECKBOXES and all the values are false
		if (type === FIELD_TYPES.CAYUSE_CHECKBOXES) {
			if (Object.values(fieldValue).every(value => !value)) {
				isEmpty = true;
			}
		}
	}

	return isEmpty;
};

export const groupFieldSections = formElements => {
	const sections = {};

	formElements.forEach(({ id, section }) => {
		const sectionLabel = section;
		const slug = slugifyString(sectionLabel);

		if (!sections[slug]) {
			sections[slug] = {
				slug,
				label: sectionLabel,
				fieldIds: []
			};
		}

		sections[slug].fieldIds.push(id);
	});

	return sections;
};

export const fetchForm = async formModelId => {
	const form = await api.form.get({ id: formModelId });

	form.formElements = form.elements;
	delete form.elements;
	const formSections = groupFieldSections(form.formElements);

	form.rules = form.rules.map(rule => ({
		...rule,
		id: generateId("rule")
	}));

	return {
		...form,
		formSections
	};
};

export const getFormElementsByIds = (formElements, fieldIds) => {
	const extractedFormElements = [];

	if (isNonEmptyArray(fieldIds)) {
		fieldIds.forEach(fieldId => {
			const match = formElements.find(({ id }) => id === fieldId);

			if (match) {
				extractedFormElements.push(match);
			}
		});
	}

	return extractedFormElements;
};

export const getFormElementDuplicatesByKey = (formElemnts, keyName) => {
	const duplicateIds = [];

	for (const formElement of formElemnts) {
		if (formElement[keyName]) {
			duplicateIds.push(formElement[keyName]);
		}
	}

	return filter(duplicateIds, (val, i, iteratee) => includes(iteratee, val, i + 1));
};

const getInvalidElementInfo = element => {
	const formElementSectionLabel = element.section || "Section";
	const formElementLabel = element.label || "Question without label";

	return ({
		formElement: element,
		formElementLabel,
		formElementSectionLabel,
		formElementErrorMessage: `${formElementSectionLabel}: ${formElementLabel}`
	});
};

const findElementsWithTypeInModelJson = (modelJson, type, sectionLabel = null, isSimpleFormBuilder = false) => {
	let foundFormElements = [];

	if (isSimpleFormBuilder) {
		if (isNonEmptyArray(modelJson?.extraSettings?.sections)) {
			for (const section of modelJson.extraSettings.sections) {
				if (isNonEmptyObject(section.extraSettings.layoutDefinition.rows)) {
					for (const row of section.extraSettings.layoutDefinition.rows) {
						for (const col of row.cols) {
							if (col.type === type) {
								foundFormElements.push({
									...col,
									sectionLabel: section.label,
								});
							}
						}
					}
				}
			}
		}
	} else {
		if (isNonEmptyArray(modelJson?.rows)) {
			for (let row of modelJson.rows) {
				if (isNonEmptyArray(row?.cols)) {
					for (let col of row.cols) {
						if (col.type === type) {
							foundFormElements.push({
								...col,
								sectionLabel,
							});
						}
						if (col.type === "tabs" && isNonEmptyArray(col?.extraSettings?.sections)) {
							for (let section of col.extraSettings.sections) {
								if (section?.type === "panel" && isNonEmptyObject(section?.extraSettings?.layoutDefinition)) {
									foundFormElements = [
										...foundFormElements,
										...findElementsWithTypeInModelJson(
											section.extraSettings.layoutDefinition,
											type,
											section.label,
											isSimpleFormBuilder,
										),
									];
								}
							}
						}
					}
				}
			}
		}
	}

	return foundFormElements;
};

const MAXIMUM_BY_CODE_TYPE = {
	FOR: 99,
	SEO: 99,
	TOA: 4,
	HERDC: 26,
};

const validateClassificationCode = extraSettings => {
	const codeType = extraSettings?.codeType;
	const trueMinimum = 1;
	const trueMaximum = MAXIMUM_BY_CODE_TYPE?.[codeType];
	if (!isNumber(trueMaximum)) {
		return "Invalid code type";
	}
	const minimum = extraSettings?.[`${codeType.toLowerCase()}Minimum`];
	const maximum = extraSettings?.[`${codeType.toLowerCase()}Maximum`];
	if (!isNumber(minimum) || minimum < trueMinimum) {
		return "Minimum out of range";
	}
	if (!isNumber(maximum) || maximum > trueMaximum || maximum < minimum) {
		return "Maximum out of range";
	}

	return false;
};

const getInvalidClassificationCodeElements = (modelJson, isSimpleFormBuilder) => {
	const invalidElements = [];
	const classificationCodeElements = findElementsWithTypeInModelJson(
		modelJson,
		"classificationCodes",
		null,
		isSimpleFormBuilder,
	);

	if (isNonEmptyArray(classificationCodeElements)) {
		for (let classificationCodeElement of classificationCodeElements) {
			const extraSettings = classificationCodeElement.extraSettings;
			const error = validateClassificationCode(extraSettings);
			if (error) {
				invalidElements.push({
					section: classificationCodeElement.sectionLabel,
					label: classificationCodeElement.label,
					issues: [ error ],
				});
			}
		}
	}

	return invalidElements;
};

const findElementInModelJsonBy = (modelJson, callback, sectionLabel = null, isSimpleFormBuilder = false) => {
	let foundFormElements = [];

	if (isSimpleFormBuilder) {
		if (isNonEmptyArray(modelJson?.extraSettings?.sections)) {
			for (const section of modelJson.extraSettings.sections) {
				if (isNonEmptyObject(section.extraSettings.layoutDefinition.rows)) {
					for (const row of section.extraSettings.layoutDefinition.rows) {
						for (const col of row.cols) {
							const truthy = callback(col);
							if (truthy) {
								foundFormElements.push({ ...col, sectionLabel: section.label });
							}
						}
					}
				}
			}
		}
	} else {
		if (isNonEmptyArray(modelJson?.rows)) {
			for (let row of modelJson.rows) {
				if (isNonEmptyArray(row?.cols)) {
					for (let col of row.cols) {
						const truthy = callback(col);
						if (truthy) {
							foundFormElements.push({ ...col, sectionLabel });
						}
						if (col.type === "tabs" && isNonEmptyArray(col?.extraSettings?.sections)) {
							for (let section of col.extraSettings.sections) {
								if (section?.type === "panel" && isNonEmptyObject(section?.extraSettings?.layoutDefinition)) {
									foundFormElements = foundFormElements.concat(
										findElementInModelJsonBy(
											section.extraSettings.layoutDefinition,
											callback,
											section.label,
											isSimpleFormBuilder
										)
									);
								}
							}
						}
					}
				}
			}
		}
	}

	return foundFormElements;
};

const getElementsWithInvalidFieldStates = (modelJson, isSimpleFormBuilder) => {
	const invalidElements = findElementInModelJsonBy(
		modelJson,
		col => {
			return col.ignore === "{{}}" || col.visible === "{{}}" || col.enabled === "{{}}";
		},
		null,
		isSimpleFormBuilder,
	);

	return invalidElements.map(element => {
		let item = { section: element.sectionLabel, label: element.label };
		if (element.ignore === "{{}}") {
			item.issues = [ VALIDATION_ERROR_IGNORE_ATTR_INVALID_VALUE ];
		}
		else if (element.visible === "{{}}") {
			item.issues = [ VALIDATION_ERROR_VISIBLE_ATTR_INVALID_VALUE ];
		}
		else if (element.enabled === "{{}}") {
			item.issues = [ VALIDATION_ERROR_ENABLED_ATTR_INVALID_VALUE ];
		}

		return item;
	});
};

export const getInvalidFormElements = (formElements, modelJson, isSimpleFormBuilder) => {
	let invalidElements = getInvalidClassificationCodeElements(modelJson, isSimpleFormBuilder);
	const elementsWithDecodedIds = formElements.map(formElement => {
		const decodedId = decodeFlowableId(formElement.id);
		formElement.decodedId = uuidValidate(decodedId) ? BLANK_KEY : decodedId;

		return formElement;
	});

	invalidElements = invalidElements.concat(
		getElementsWithInvalidFieldStates(modelJson, isSimpleFormBuilder)
	);

	const duplicateIds = getFormElementDuplicatesByKey(elementsWithDecodedIds, "decodedId").filter(element => element !== BLANK_KEY);

	const elementsWithBlankIds = elementsWithDecodedIds.filter(element => !element["id"] || element["decodedId"] === BLANK_KEY);
	addInvalidElements(invalidElements, elementsWithBlankIds, VALIDATION_ERROR_BLANK_KEY);

	const elementsWithBlankLabels = elementsWithDecodedIds.filter(element => !element["headerId"]);
	addInvalidElements(invalidElements, elementsWithBlankLabels, VALIDATION_ERROR_BLANK_LABEL);

	const elementsWithValueStartsWithNumber = elementsWithDecodedIds.filter(element => element.value.match(/^\d/));
	addInvalidElements(invalidElements, elementsWithValueStartsWithNumber, VALIDATION_ERROR_VALUE_STARTS_WITH_NUMBER);

	duplicateIds.forEach(dupeId => {
		const dupeElements = elementsWithDecodedIds.filter(element => element["decodedId"] === dupeId && element["decodedId"] !== BLANK_KEY);
		addInvalidElements(invalidElements, dupeElements,  VALIDATION_ERROR_DUPLICATE_KEY );
	});

	return invalidElements;
};

const addInvalidElements = (invalidElements, elements, errorType) => {
	if(isNonEmptyArray(elements)) {
		elements.map(invalidFormElement => {
			const element = getInvalidElementInfo(invalidFormElement);
			const existingInvalidElement = invalidElements.find(invalidElement => invalidElement.section === element.formElementSectionLabel && invalidElement.label === element.formElementLabel );
			if (existingInvalidElement && !existingInvalidElement.issues.includes(errorType)) {
				existingInvalidElement.issues.push(errorType);
			}else{
				invalidElements.push({
					section: element.formElementSectionLabel,
					label:element.formElementLabel,
					issues: [ errorType ]
				});
			}
		});
	}
};

export const getResearchTeamFormElement = formElements => {
	return isNonEmptyArray(formElements)
		? formElements.find(({ cayuseType }) => cayuseType === CAYUSE_TYPES.RESEARCH_TEAM)
		: undefined;
};

export const isFieldUnitFinder = field => isObjectWithKey(field, "cayuseType") && field.cayuseType === "unit-finder";

export const isFieldOrgFinder = field => isObjectWithKey(field, "cayuseType") && field.cayuseType === "org-finder";

export const isFieldPeopleFinder = field => isObjectWithKey(field, "cayuseType") && field.cayuseType === "person-finder";

export const getFieldRestQueryUrl = field => {
	if (!isObjectWithKey(field, "dataSource")) {
		return null;
	} else if (field.dataSource.type !== "Rest") {
		console.warn("unsupported dataSource type for dynamic lookup provided: ", field.dataSource.type);

		return null;
	}

	const cayuseUrl = "api"; // TODO use global api constant?
	const searchText = "";

	const searchApiUrl = spUrl(field.dataSource.restQueryUrl)
		.replace("{{endpoints.cayuse}}", cayuseUrl)
		.replace("{{$searchText}}", searchText);

	return searchApiUrl;
};

export const mapFormElementRows = ({ rows, lastPublishedFormElements = [] }) => rows.map(row => {
	row.cols.forEach(col => {
		const id = col.newId;

		if (id && lastPublishedFormElements.includes(id)) {
			col.keyDisabled = true;
		}
	});

	return row;
});

export const mapFormElementCol = ({ col = {}, lastPublishedFormElements = [] }) => {
	const id =  col.newId;

	if (id && lastPublishedFormElements.includes(id)) {
		col.keyDisabled = true;
	}

	return col;
};

export const extractQuestionsFromRows = rows => {
	let questions = [];

	(rows || []).forEach(row => {
		const { cols = [] } = row;

		cols.forEach(col => {
			questions.push(col);
		});
	});

	return questions;
};

export const extractQuestionsFromSection = section => {
	const questions = [];
	for (const row of section.extraSettings.layoutDefinition.rows) {
		for (const question of row.cols) {
			if (question.id) {
				question.section = section.label;
				questions.push(question);
			}
		}
	}

	return questions;
};

export const getLastPublishedFormElements = async key => {
	try {
		const response = await api.form.getLastPublishedElements(key);
		const data = await api.form.getModel(response.data[0].id);

		const sections = data.rows[0].cols[0].extraSettings.sections;
		let result = [];
		for (const section of sections) {
			result = result.concat(extractQuestionsFromSection(section));
		}

		return result;
	} catch {
		// todo: handle error
		return null;
	}
};

export const getFormType = modelKey => {
	switch(modelKey) {
	case "proposalForm":
		return COLUMN_HEADER_KEYS.PROPOSAL;
	case "awardForm":
		return COLUMN_HEADER_KEYS.AWARD;
	case "subawardForm":
		return COLUMN_HEADER_KEYS.SUBAWARD;
	case "budgetBuilderForm":
		return COLUMN_HEADER_KEYS.BUDGET;
	default:
		return undefined;
	}
};

export const isModification = form =>
	!!form.modificationNumber && parseInt(form.modificationNumber) > 0;

export const encodeFlowableId = id => id ? `${uuidv4()}:${id}` : uuidv4();

export const decodeFlowableId = id => {
	if (isNonEmptyString(id)) {
		const [ uuid, userProvidedId ] = id.split(":");
		const isValidUuid = uuidValidate(uuid);

		if (isValidUuid && userProvidedId) {
			return userProvidedId;
		} else {
			return id;
		}
	}

	return id;
};

export const findElementByValue = (formModel, value) => {
	let formElement;

	if (isNonEmptyArray(formModel.rows)) {
		deepFind: for (const row of formModel.rows) {
			for (const col of row.cols) {
				for (const section of col.extraSettings.sections) {
					for (const sectionRow of section.extraSettings.layoutDefinition.rows) {
						for (const field of sectionRow.cols) {
							if (field.value === value) {
								formElement = field;

								break deepFind;
							}
						}
					}
				}
			}
		}
	}

	return formElement;
};

const fixValueType = col => {
	if (col.type === "upload" && col.extraSettings.allowDelete === true) {
		col.extraSettings.allowDelete = "true";
	}

	if (col.type === "upload" && col.extraSettings.allowDelete === false) {
		col.extraSettings.allowDelete = "false";
	}

	if (
		col.extraSettings &&
		isNonEmpty(col.extraSettings.numberType) &&
		(col.extraSettings.numberType === "integer" || col.extraSettings.numberType === "float") &&
		col.defaultValue === 0
	) {
		col.defaultValue = col.defaultValue.toString();
	}
};

const MATCH_OPERATORS = {
	all: "&&",
	any: "||"
};

const buildExpression = node => {
	if (!node) {
		return "";
	}

	if (node.expression) {
		return `(${node.expression})`;
	}

	if (node.sourceValue) {
		const { name: left } = node.sourceValue;
		const { operator = "", targetValue } = node;

		if (targetValue) {
			const value = targetValue.value ?? "";
			const right = targetValue.type === "string" ? `'${value}'` : value;

			return `(${left} ${operator} ${right})`;
		}

		return operator ? `(${operator}(${left}))` : left;
	}

	if (isNonEmptyArray(node.children)) {
		const childExpressions = node.children.map(buildExpression).filter(Boolean);

		return node.match === "none"
			? `!(${childExpressions.join(` ${MATCH_OPERATORS.any} `)})`
			: `(${childExpressions.join(` ${MATCH_OPERATORS[node.match] || MATCH_OPERATORS.all} `)})`;
	}

	return "";
};

const fixExpression = (col, fieldName) => {
	const conditionBuilderExpression = col.designInfo?.conditionBuilderInfo?.[fieldName];
	const hasExpressionTree = isObject(col[fieldName]) || conditionBuilderExpression;

	if (hasExpressionTree) {
		const expressionTree = isObject(col[fieldName]) ? col[fieldName] : conditionBuilderExpression;
		const expression = buildExpression(expressionTree);

		col[fieldName] = `{{${expression}}}`;

		// clean up designInfo.conditionBuilderInfo so Flowable doesn't overwrite the fixed expression
		if (conditionBuilderExpression) {
			delete col.designInfo.conditionBuilderInfo[fieldName];
		}
	}

	return col;
};

export const fixExpressionAttributes = col => {
	if (col.type === "expressionField") {
		fixExpression(col, "expression");
	}
	fixExpression(col, "visible");
	fixExpression(col, "enabled");
	fixExpression(col, "ignore");
	fixExpression(col, "isRequired");
};

export const decodeFormModelIds = formModel => {
	if (isNonEmptyArray(formModel.rows)) {
		formModel.rows.forEach(row => {
			if (isNonEmptyArray(row.cols)) {
				row.cols.forEach(col => {
					if (isNonEmptyArray(col.extraSettings?.sections)) {
						col.extraSettings.sections.forEach(section => {
							if (isNonEmptyArray(section.extraSettings?.layoutDefinition?.rows)) {
								section.extraSettings.layoutDefinition.rows.forEach(row => {
									if (isNonEmptyArray(row.cols)) {
										row.cols.forEach(col => {
											fixValueType(col);
											fixExpressionAttributes(col);
											col.id = decodeFlowableId(col.id);
										});
									}
								});
							}
						});
					}
				});
			}
		});
	}

	return formModel;
};

/**
 * Recursively traverses the form structure to extract field values matching the given types.
 * @param {Object} form
 * @param {string[]} [types=["number","one"]] - Array of field types to collect
 * @returns {string[]} Collected field values (bracket expressions are unwrapped)
 */
export const getAllFieldsForType = (form, types) => {
	const numberValues = [];

	const stripBrackets = val => {
		if (typeof val === "string" && val.startsWith("{{") && val.endsWith("}}")) {
			return val.slice(2, -2).trim();
		}

		return val;
	};

	const traverseRows = rows => {
		if (!Array.isArray(rows)) {return;}

		rows.forEach(row => {
			if (!Array.isArray(row.cols)) {return;}

			row.cols.forEach(col => {
				if (types.includes(col.type)) {
					numberValues.push(stripBrackets(col.value));
				}
				if (Array.isArray(col.extraSettings?.sections)) {
					col.extraSettings.sections.forEach(section => {
						traverseRows(section.extraSettings?.layoutDefinition?.rows);
					});
				}
				if (Array.isArray(col.extraSettings?.layoutDefinition?.rows)) {
					traverseRows(col.extraSettings.layoutDefinition.rows);
				}
			});
		});
	};

	traverseRows(form.rows);

	return numberValues;
};

export const checkPublishedFormElements = (lastPublishedIds, formModel) => {
	if (isNonEmptyArray(formModel.rows)) {
		formModel.rows.forEach(row => {
			if (isNonEmptyArray(row.cols)) {
				row.cols.forEach(col => {
					if (isNonEmptyArray(col.extraSettings?.sections)) {
						col.extraSettings.sections.forEach(section => {
							if (isNonEmptyArray(section.extraSettings?.layoutDefinition?.rows)) {
								section.extraSettings.layoutDefinition.rows.forEach(row => {
									if (isNonEmptyArray(row.cols)) {
										row.cols.forEach(col => {
											if (lastPublishedIds?.includes(col.id)) {
												col.keyDisabled = true;
											} else {
												col.keyDisabled = false;
											}
										});
									}
								});
							}
						});
					}
				});
			}
		});
	}

	return formModel;
};

export const mapFieldTypeToStencilInfo = ({ type, cayuseType }) => {
	switch (type) {
	case "text":
		return {
			customStencilId: "null",
			stencilId: "cloud-text",
			stencilSuperIds: [ "base-text", "Component" ]
		};
	case "richText":
		return {
			customStencilId: "null",
			stencilId: "cloud-rich-text",
			stencilSuperIds: [ "base-rich-text", "Component" ]
		};
	case "textarea":
		return {
			customStencilId: "null",
			stencilId: "cloud-text-area",
			stencilSuperIds: [ "base-text-area", "Component" ]
		};
	case "date":
		return {
			customStencilId: "null",
			stencilId: "cloud-date",
			stencilSuperIds: [ "base-date", "Component" ]
		};
	case "radio":
		return {
			customStencilId: "null",
			stencilId: "cloud-radiobuttons",
			stencilSuperIds: [ "base-radiobuttons", "Component" ]
		};
	case "select":
	case "person-finder":
	case "unit-finder":
	case "org-finder":
		return cayuseType ? mapCayuseFieldTypeToStencilInfo(cayuseType) : {
			customStencilId: "null",
			stencilId: "cloud-single-select",
			stencilSuperIds: [ "base-single-select", "Component" ]
		};
	case "upload":
		return {
			customStencilId: "null",
			stencilId: "cloud-attachment",
			stencilSuperIds: [ "base-attachment", "Component" ]
		};
	case "one":
	case "number":
		return {
			customStencilId: "null",
			stencilId: "cloud-integer-number",
			stencilSuperIds: [ "IntegerNumber" ]
		};
	default:
		return null;
	}
};

const mapCayuseFieldTypeToStencilInfo = cayuseType => {
	switch (cayuseType) {
	case "person-finder":
	case "unit-finder":
	case "org-finder":
		return {
			customStencilId: "null",
			stencilId: `cayuse-${cayuseType}`,
			stencilSuperIds: [ "base-multi-select", "Component" ]
		};
	default:
		return null;
	}
};

export const mapCayuseTypeToFieldType = ({ type, cayuseType }) => {
	switch (cayuseType) {
	case "person-finder":
	case "unit-finder":
	case "org-finder":
		return "select";
	case "research-team":
		return "researchTeam";
	case "cayuse-irb-study":
		return "cayuseIrbStudy";
	case "cayuse-summary-budget-importer":
		return "cayuseSummaryBudgetImporter";
	case "cayuse-proposals-s2s-record":
		return "cayuseProposalsS2SRecord";
	case "cayuse-human-ethics-study-search":
		return "cayuseHumanEthicsStudySearch";
	case "cayuse-animal-oversight":
		return "cayuseAnimalOversight";
	case "cayuse-hazard-safety":
		return "cayuseHazardSafety";
	case "cayuse-haplo-costing-tool":
		return "cayuseHaploCostingTool";
	case "cayuse-haplo-ethics-monitor":
		return "cayuseHaploEthicsMonitor";
	case "cayuse-haplo-repository":
		return "cayuseHaploRepository";
	case "cayuse-disclosure-form-component":
		return "cayuseCOIComponent";
	case "expression-field":
		return "expressionField";
	case "upload":
		return "fileInput";
	default:
		return type;
	}
};

export const  getKeyboardFocusableElements = (element = document) => {
	return [ ...element.querySelectorAll(
	  "a[href], button, input, textarea, select, details, [tabindex]:not([tabindex=\"-1\"])"
	) ]
	  .filter(el => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
};

const assembleExtraSettings = (formElement, options) => {
	const rawExtraSettings = isNonEmptyArray(formElement.extraSettings)
		? formElement.extraSettings
		: [];

	const extraSettings = rawExtraSettings.reduce((cumulativeSettings, currentSetting) => {
		return {
			...cumulativeSettings,
			...currentSetting,
		};
	}, {});

	let type = extraSettings.timeEnabled && formElement.type === FIELD_TYPES.DATE ? FIELD_TYPES.DATETIME : formElement.type;

	if (options.mapType) {
		// using in proposal object
		type = mapType(type);
	}

	return {
		...formElement,
		type,
		extraSettings,
	};
};

export const assembleFormSectionsAndElements = (sourceFormElements, options = {}) => {
	const formSections = {};
	const formElements = [];

	if (isNonEmptyArray(sourceFormElements)) {
		sourceFormElements.forEach(element => {
			const formSection = element.section;

			if (isNonEmptyString(formSection)) {
				if (!formSections[formSection]) {
					formSections[formSection] = {
						label: element.section,
						headerId: element.headerId,
						fieldIds: [],
						fieldHeaderIds: []
					};
				}

				formElements.push(assembleExtraSettings(element, options));
				formSections[formSection].fieldIds.push(element.id);
				formSections[formSection].fieldHeaderIds.push(element.headerId);
			}
		});
	}

	return {
		formElements,
		formSections: Object.values(formSections),
	};
};

export const dateNeedsToBeUpdated = column =>
	column.type === "date" &&
    !column.extraSettings.enableTime &&
    !column.extraSettings.storeWithoutTime;

export const attachmentsNeedsToBeUpdated = column =>
	column.type === "upload" &&
		!column.extraSettings.allowDelete;

export const transformFormElement = col => {
	col.tooltip = col.tooltip || col.extraSettings?.tooltip || "";
	col.type = mapType(col.type);
	if(col.type === FIELD_TYPES.DATE) {
		col.extraSettings = getDateFormat(col.extraSettings);
		if (col.extraSettings && !col.extraSettings?.enableTime) {
			col.extraSettings.storeWithoutTime = true;
		}
	}

	if(col.type === FIELD_TYPES.SELECT && col.cayuseType === CAYUSE_TYPES.PERSON_FINDER) {
		col.type = "personFinder";
	}
	if(col.type === FIELD_TYPES.SELECT && col.cayuseType === CAYUSE_TYPES.UNIT_FINDER) {
		col.type = "unitFinder";
	}
	if(col.type === FIELD_TYPES.SELECT && col.cayuseType === CAYUSE_TYPES.ORG_FINDER) {
		col.type = "orgFinder";
	}

	// Fix for label position
	addCheckboxLabelAlign(col);

	return col;
};

const addCheckboxLabelAlign = col => {
	if (col && col.type === "boolean" && isEmpty(col.checkboxLabelAlign)) {
		col.checkboxLabelAlign = col.labelAlign;
	}
};

export const getUnmatchedRules = async (rules, formElements, modelKey) => {
	let rulesWithoutMatch = [];
	const publishedFormElements = await getLastPublishedFormElements(modelKey);

	rules.forEach(rule => {
		(rule.condition?.operands ?? []).forEach(group => {
			(group.operands ?? []).forEach(operand => {
				const formElement = formElements.some(formElement => formElement.id === operand.operand?.formElementId);
				const alreadyPresent = rulesWithoutMatch
					.some(existingRule => existingRule.rule.id === rule.id
						&& (!existingRule.formElement || existingRule.formElement.id === operand.operand?.formElementId)
					);
				if (!formElement && !alreadyPresent) {
					rulesWithoutMatch.push({
						rule,
						formElement: publishedFormElements.find(element => element.id === operand.operand?.formElementId)
					});
				}
			});
		});
	});

	return { rulesWithoutMatch, publishedFormElements };
};

export const getRulesWithUnknownTeams = async rules => {
	const adHocTeamRules = rules
		.filter(rule => [
			RULE_TYPES.AD_HOC_REVIEW,
			RULE_TYPES.INFOREADY_REVIEW,
			RULE_TYPES.AD_HOC_NOTIFICATION
		].includes(rule.ruleType))
		.filter(rule => rule.targetId);

	const uniqueTargetIds = Array.from(new Set(adHocTeamRules.map(rule => rule.targetId)));

	const teamResults = await Promise.all(
		uniqueTargetIds.map(async targetId => {
			const team = await findAdHocTeamById(targetId);

			return { targetId, teamExists: !!team };
		})
	);

	const teamExistsMap = new Map(
		teamResults.map(({ targetId, teamExists }) => [ targetId, teamExists ])
	);

	return adHocTeamRules.filter(rule => !teamExistsMap.get(rule.targetId));
};

export const validateFormData = (modelJson, errorMsg) => {
	return (findElementInModelJsonBy(
		modelJson,
		col => (!col.value),
		null,
		false,
	) ?? [])
		.filter(element =>
			Object.values(FIELD_TYPES).includes(element.type) || Object.values(CAYUSE_TYPES).includes(element.cayuseType))
		.map(element => ({
			...element,
			section: element.sectionLabel,
			issues: [ errorMsg ]
		}));
};

export const getDataTableMissingCount = ({ formContent, key, columns }) => {
	const value = formContent?.[key];
	const requiredColumns = columns?.filter(column => column.component.required) ?? [];
	if (!Array.isArray(columns) || !requiredColumns.length) {return 0;}

	if(!value) {
		return requiredColumns.length;
	}

	let missingCount = 0;
	value.rows.forEach(row => {
		requiredColumns.forEach(requiredColumn => {
			const column = row.columns.find(column => column.name === requiredColumn.label);
			if (column) {
				// const errors = validateTableField(column.value, requiredColumn.id, requiredColumn.component?.required, requiredColumn.component?.["minimum-length"], requiredColumn.component?.["maximum-length"]);
				let errors = [];
				validateIsRequired(column.value, requiredColumn.component?.required, errors);
				if (errors.length) {
					missingCount ++;
				}
			} else {
				missingCount++;
			}
		});
	});

	return missingCount;
};
